/**
 * Gating predicates — Multi-Add Phase 2 (spec §6).
 *
 * Product rule (engineer, spec §6): a device cannot perform ANY action —
 * recording, RTMP, NDI, movement/PTZ, joystick, routing-to-production —
 * until it is authenticated. Two device shapes are "not yet trustworthy":
 *
 *   1. A *transitory placeholder* (spec §2): a frontend-only, never-
 *      committed synthetic device for an unauthenticated bulk-add camera.
 *      It carries `transitoryKind` (`device/transitory.ts::toSyntheticDevice`)
 *      and has no proxy, so commands that use `locateProxy` already no-op
 *      naturally — but the UI affordances must still be suppressed so they
 *      never render over the placeholder tile.
 *
 *   2. A *committed device whose stream auth is INVALID* (spec §6
 *      "post-commit"): a real device that DID commit but whose RTSP /
 *      control driver rejected its credentials (401). Its proxy EXISTS, so
 *      the natural no-op does NOT protect it — it must be gated explicitly
 *      by `authenticationStatus === INVALID`. This also covers a previously
 *      healthy device whose creds were rotated ("general" scope, engineer
 *      decision).
 *
 * The single gating predicate is therefore `isTransitory(device) ||
 * isAuthInvalid(device)` (spec §6). This module is the single source of
 * truth for it (`think-in-systems`, ai.knowledge/global) so every
 * chokepoint — commands, the controls Guard, the canvas <Interaction>, and
 * the production source lists — applies the SAME rule rather than
 * re-deriving it.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 *
 * INVALID-detection mirrors the per-aspect read already used by
 * `room/components/video/sources/item/chip-state.ts` (the "Authenticate"
 * chip): video and control (movement) carry independent
 * `AuthenticationStatus`, and EITHER aspect INVALID gates the device.
 */
import { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { getTransitoryDevice } from '#~/device/transitory.js';
import type { Device } from '#~/device/index.js';

/**
 * The subset of fields the gating predicate reads. Accepting a structural
 * subset (rather than the full `Device`) keeps the predicate testable with
 * plain object literals — no proxy / state-sync graph required (per
 * `esm-jest-mock-patterns.md`: avoid pulling the non-jest-executable device
 * tree) — and lets a synthetic transitory device (which carries only
 * `transitoryKind`) be passed verbatim.
 */
export type Gateable = T_Maybe<{
  /** Set ONLY on a synthetic transitory placeholder (spec §2). */
  readonly transitoryKind?: T_Maybe<string>;

  /** Per-aspect video credential-validation status (spec §6). */
  readonly videoAuthenticationStatus?: T_Maybe<AuthenticationStatus>;

  /** Per-aspect control credential-validation status (spec §6). */
  readonly controlAuthenticationStatus?: T_Maybe<AuthenticationStatus>;
}>;

/**
 * True when `device` is an unauthenticated bulk-add placeholder (spec §2).
 * A real device never carries `transitoryKind`, so the marker's presence is
 * the discriminator (`toSyntheticDevice`, `device/transitory.ts`).
 */
export const isTransitory = (device: Gateable): boolean => device?.transitoryKind != null;

/**
 * True when EITHER aspect's credentials were rejected (spec §6 post-commit).
 * Mirrors `chip-state.ts`'s priority-2 read: video and control carry
 * independent `AuthenticationStatus`; either INVALID gates the device.
 */
export const isAuthInvalid = (device: Gateable): boolean =>
  device?.videoAuthenticationStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID ||
  device?.controlAuthenticationStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID;

/**
 * The gating predicate (spec §6): a placeholder OR a committed device whose
 * stream auth is INVALID can do NOTHING until authenticated. Every UI / command
 * chokepoint suppresses its affordance when this is true.
 */
export const isDeviceGated = (device: Gateable): boolean =>
  isTransitory(device) || isAuthInvalid(device);

/**
 * Imperative, id-only gate for the command layer (recording / RTMP / NDI /
 * movement), which receives a `deviceId` and runs OUTSIDE React. Resolves
 * the transitory store directly (`getTransitoryDevice` reads `getState()`).
 *
 * NOTE: this catches the PLACEHOLDER case only. A committed-but-INVALID
 * device has a proxy and is NOT in the transitory store, so it is gated at
 * the UI layer (the affordance never renders) rather than at the command —
 * see the production source-list filters + the controls/interaction guards.
 */
export const isTransitoryId = (deviceId: T_Maybe<Device['id']>): boolean =>
  getTransitoryDevice(deviceId) != null;
